import { reactive } from 'vue'
import axios from 'axios'
import { API_ENDPOINTS } from '@/config/api.endpoints'
import {
  CurrencyCatalogEntry,
  FilingCountry,
  FilingCountryPatchRequest,
  FilingCountryTemplatePatchRequest,
  OandaDataSet,
} from '@/types/filingCountry'
import { AuthState } from '@/types/authState'
import { ApiError } from '@/util/ApiError'
import { normalizeError } from '@/util/errorNormalizer'

const FILING_COUNTRY_CONFIG = API_ENDPOINTS.FILING_COUNTRY

export enum API_STATES {
  INITIAL = 'INITIAL',
  LOADING = 'LOADING',
  ERROR = 'ERROR',
  COMPLETE = 'COMPLETE',
}

interface FilingCountryConfigApi {
  state: API_STATES
  error: ApiError | null
  merchantIds: string[]
  filingCountries: FilingCountry[]
  getFilingCountries: (authState: AuthState) => Promise<FilingCountry[]>
  updateFilingCountry: (
    authState: AuthState,
    filingCountry: FilingCountry,
  ) => Promise<FilingCountry[] | undefined>
  patchFilingCountryTemplate: (
    authState: AuthState,
    countryCode: string,
    templateUpdate: FilingCountryTemplatePatchRequest,
  ) => Promise<FilingCountry>
  getCurrencies: (authState: AuthState) => Promise<CurrencyCatalogEntry[]>
  getOandaDataSets: (authState: AuthState) => Promise<OandaDataSet[]>
  patchFilingCountry: (
    authState: AuthState,
    countryCode: string,
    patch: FilingCountryPatchRequest,
  ) => Promise<FilingCountry>
}

// available as global state
export const filingCountryConfigApi: FilingCountryConfigApi = reactive({
  state: API_STATES.INITIAL,
  error: null,
  merchantIds: [],
  filingCountries: [],
  getFilingCountries(authState: AuthState): Promise<FilingCountry[]> {
    const token = authState.user?.accessToken
    filingCountryConfigApi.state = API_STATES.LOADING
    filingCountryConfigApi.error = null
    return axios
      .get<FilingCountry[]>(FILING_COUNTRY_CONFIG.ALL_FILING_COUNTRIES, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(({ data }) => {
        if (Array.isArray(data)) {
          filingCountryConfigApi.state = API_STATES.COMPLETE
          filingCountryConfigApi.filingCountries = data
          return filingCountryConfigApi.filingCountries
        }
        throw Error('Unexpected response')
      })
      .catch(err => {
        filingCountryConfigApi.state = API_STATES.ERROR
        filingCountryConfigApi.error = normalizeError(err)
        throw filingCountryConfigApi.error
      })
  },
  updateFilingCountry(
    authState: AuthState,
    filingCountry: FilingCountry,
  ): Promise<FilingCountry[] | undefined> {
    const token = authState.user?.accessToken
    filingCountryConfigApi.error = null
    if (!filingCountry.CountryCode) return Promise.resolve(undefined)
    return axios
      .put<FilingCountry>(
        `${FILING_COUNTRY_CONFIG.UPDATE_FILING_COUNTRY}/${filingCountry.CountryCode}`,
        filingCountry,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      .then(({ data }) => {
        if (data) {
          const index = filingCountryConfigApi.filingCountries.findIndex(
            fc => fc.CountryCode === data.CountryCode,
          )
          if (index !== -1) {
            filingCountryConfigApi.filingCountries[index] = data
          }
          return filingCountryConfigApi.filingCountries
        }
        throw Error('Unexpected response')
      })
      .catch(err => {
        filingCountryConfigApi.error = normalizeError(err)
        throw filingCountryConfigApi.error
      })
  },
  patchFilingCountryTemplate(
    authState: AuthState,
    countryCode: string,
    templateUpdate: FilingCountryTemplatePatchRequest,
  ): Promise<FilingCountry> {
    const token = authState.user?.accessToken
    filingCountryConfigApi.error = null
    return axios
      .patch<FilingCountry>(
        `${FILING_COUNTRY_CONFIG.PATCH_FILING_COUNTRY}/${countryCode}`,
        templateUpdate,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      )
      .then(({ data }) => {
        const index = filingCountryConfigApi.filingCountries.findIndex(
          fc => fc.CountryCode === data.CountryCode,
        )
        if (index !== -1) {
          filingCountryConfigApi.filingCountries[index] = data
        }
        return data
      })
      .catch(err => {
        filingCountryConfigApi.error = normalizeError(err)
        throw filingCountryConfigApi.error
      })
  },
  getCurrencies(authState: AuthState): Promise<CurrencyCatalogEntry[]> {
    const token = authState.user?.accessToken
    return axios
      .get<CurrencyCatalogEntry[]>(FILING_COUNTRY_CONFIG.GET_CURRENCIES, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(({ data }) => data)
      .catch(err => {
        throw normalizeError(err)
      })
  },
  getOandaDataSets(authState: AuthState): Promise<OandaDataSet[]> {
    const token = authState.user?.accessToken
    return axios
      .get<OandaDataSet[]>(FILING_COUNTRY_CONFIG.GET_OANDA_DATA_SETS, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(({ data }) => data)
      .catch(err => {
        throw normalizeError(err)
      })
  },
  /**
   * Combined PATCH for a filing country row.
   * Accepts any subset of the five editable fields (CurrencyCode, OandaDataSet,
   * IncludedIn*Template). Only the changed fields need to be included.
   * Updates the reactive cache on success.
   */
  patchFilingCountry(
    authState: AuthState,
    countryCode: string,
    patch: FilingCountryPatchRequest,
  ): Promise<FilingCountry> {
    const token = authState.user?.accessToken
    filingCountryConfigApi.error = null
    return axios
      .patch<FilingCountry>(
        `${FILING_COUNTRY_CONFIG.PATCH_FILING_COUNTRY}/${countryCode}`,
        patch,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      )
      .then(({ data }) => {
        const index = filingCountryConfigApi.filingCountries.findIndex(
          fc => fc.CountryCode === data.CountryCode,
        )
        if (index !== -1) {
          filingCountryConfigApi.filingCountries[index] = data
        }
        return data
      })
      .catch(err => {
        filingCountryConfigApi.error = normalizeError(err)
        throw filingCountryConfigApi.error
      })
  },
})
